import { Clear, Search, AddOutlined as AddOutlinedIcon } from "@mui/icons-material";
import {
	Box,
	Button,
	ButtonGroup, // Importado para a sub-toolbar
	FormControl,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	SelectChangeEvent,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { format, startOfMonth, endOfMonth, subDays, subMonths, subYears, addMonths, parseISO } from "date-fns";
import { useState } from "react";
import { checkByProfileGuard } from "../../../shared/guards/ProfileGuard";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../shared/enums/profiles";

// Tipagem para o novo filtro de tipo de documento
export type DocumentTypeFilter = "previa" | "prolabore" | "fatura" | "boletos";

// Interface adaptada para a barra de ferramentas Financeiras
interface FireFinancialToolbarProps {
	// Novos campos para a sub-toolbar de filtro de documentos
	documentType: DocumentTypeFilter;
	onChangeDocumentType: (value: DocumentTypeFilter) => void;

	// Novo campo Imobiliária (simulado com string, mas seria um objeto no Autocomplete)
	imobiliariaSearch?: string;
	onChangeImobiliariaSearch?: (value: string) => void;

	textSearch?: string;
	onChangeTextSearch?: (value: string) => void;
	onClearTextSearch?: () => void;
	showInputSearch?: boolean;

	showDateRange?: boolean;
	onChangeDateStart?: (value: string) => void;
	onChangeDateEnd?: (value: string) => void;

	showStatusSelect?: boolean;
	onChangeStatus?: (value: string) => void;

	showRefreshButton?: boolean;
	onClickRefresh?: () => void;

	showNewButton?: boolean; // Renomeado para ser genérico
	onClickNew?: () => void; // Renomeado para ser genérico
}

export function FireFinancialToolbar({
	documentType,
	onChangeDocumentType,
	imobiliariaSearch = "",
	onChangeImobiliariaSearch,
	textSearch = "",
	onChangeTextSearch,
	onClearTextSearch,
	showInputSearch = true,
	showDateRange = true,
	onChangeDateStart,
	onChangeDateEnd,
	showStatusSelect = false,
	onChangeStatus,
	showRefreshButton = true,
	onClickRefresh,
	showNewButton = false,
	onClickNew,
}: FireFinancialToolbarProps) {
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
	const today = new Date();

	const [startDate, setStartDate] = useState(format(startOfMonth(today), "yyyy-MM-dd"));
	const [endDate, setEndDate] = useState(format(endOfMonth(today), "yyyy-MM-dd"));
	const [status, setStatus] = useState("todos");
	const [periodType, setPeriodType] = useState("1_mes");

	// Permission Checks
	const canViewPreview = checkByProfileGuard(
		PermissionRolesEnum.FireFinancialOptions,
		PermissionActionsEnum.Preview,
	);

	const canViewInvoice = checkByProfileGuard(
		PermissionRolesEnum.FireFinancialOptions,
		PermissionActionsEnum.Invoice,
	);

	const canViewProlabore = checkByProfileGuard(
		PermissionRolesEnum.FireFinancialOptions,
		PermissionActionsEnum.Prolabore,
	);

	const canViewBoleto = checkByProfileGuard(
		PermissionRolesEnum.FireFinancialOptions,
		PermissionActionsEnum.Boleto,
	);

	const getDateRangeFromPeriod = (period: string): [string, string] => {
		const now = new Date();
		let start: Date = now;
		let end: Date = now;
		// Lógica de cálculo de período (mantida)
		switch (period) {
			case "15_dias":
				start = subDays(now, 15);
				end = now;
				break;
			case "1_mes":
				start = startOfMonth(now);
				end = endOfMonth(now);
				break;
			case "2_meses":
				start = startOfMonth(subMonths(now, 1));
				end = endOfMonth(now);
				break;
			case "3_meses":
				start = startOfMonth(subMonths(now, 2));
				end = endOfMonth(now);
				break;
			case "1_ano":
				start = startOfMonth(subYears(now, 1));
				end = endOfMonth(now);
				break;
			case "mes_anterior":
				start = startOfMonth(subMonths(now, 1));
				end = endOfMonth(subMonths(now, 1));
				break;
			case "proximo_mes":
				start = startOfMonth(addMonths(now, 1));
				end = endOfMonth(addMonths(now, 1));
				break;
		}

		return [format(start, "yyyy-MM-dd"), format(end, "yyyy-MM-dd")];
	};

	const handlePeriodChange = (e: SelectChangeEvent) => {
		const value = e.target.value;
		setPeriodType(value);

		if (value !== "personalizado") {
			const [newStart, newEnd] = getDateRangeFromPeriod(value);
			setStartDate(newStart);
			setEndDate(newEnd);
			onChangeDateStart?.(newStart);
			onChangeDateEnd?.(newEnd);
		}
	};

	const handleStatusChange = (e: SelectChangeEvent) => {
		const value = e.target.value;
		setStatus(value);
		onChangeStatus?.(value);
	};

	return (
		<Box component={Paper} display="flex" flexDirection="column" padding={2} gap={1} mx={1}>
			{/* LINHA 1: SUB-TOOLBAR DE DOCUMENTOS */}
			<Box mb={2}>
				<ButtonGroup variant="contained" aria-label="Filtro de tipo de documento financeiro" fullWidth={isSmallScreen}>
					{canViewPreview && (
						<Button onClick={() => onChangeDocumentType("previa")} variant={documentType === "previa" ? "contained" : "outlined"}>
							Prévia Fatura
						</Button>
					)}

					{canViewInvoice && (
						<Button onClick={() => onChangeDocumentType("fatura")} variant={documentType === "fatura" ? "contained" : "outlined"}>
							Fatura
						</Button>
					)}

					{canViewProlabore && (
						<Button onClick={() => onChangeDocumentType("prolabore")} variant={documentType === "prolabore" ? "contained" : "outlined"}>
							Pró-labore
						</Button>
					)}

					{canViewBoleto && (
						<Button onClick={() => onChangeDocumentType("boletos")} variant={documentType === "boletos" ? "contained" : "outlined"}>
							Boletos
						</Button>
					)}
				</ButtonGroup>
			</Box>

			{/* LINHA 2: FILTROS PRINCIPAIS */}
			<Grid container spacing={2} direction={isSmallScreen ? "column" : "row"} alignItems={isSmallScreen ? "stretch" : "center"}>
				{/* NOVO CAMPO: IMOBILIÁRIA (Autocomplete Simulado) */}
				<Grid item xs={12} sm={6} md={2}>
					<TextField
						fullWidth
						size="small"
						label="Imobiliária"
						placeholder="Busque pela imobiliária"
						value={imobiliariaSearch}
						onChange={(e) => onChangeImobiliariaSearch?.(e.target.value)}
					// Aqui você usaria o Autocomplete do MUI
					/>
				</Grid>

				{/* PERÍODO DE DATA */}
				{showDateRange && (
					<>
						<Grid item xs={12} sm={6} md={2}>
							<FormControl fullWidth size="small">
								<InputLabel id="period-label">Período</InputLabel>
								<Select labelId="period-label" value={periodType} label="Período" onChange={handlePeriodChange}>
									<MenuItem value="15_dias">Últimos 15 dias</MenuItem>
									<MenuItem value="mes_anterior">Mês Anterior</MenuItem>
									<MenuItem value="1_mes">Mês Atual</MenuItem>
									<MenuItem value="proximo_mes">Próximo Mês</MenuItem>
									<MenuItem value="2_meses">Últimos 2 meses</MenuItem>
									<MenuItem value="3_meses">Últimos 3 meses</MenuItem>
									<MenuItem value="1_ano">Último ano</MenuItem>
									<MenuItem value="personalizado">Personalizado</MenuItem>
								</Select>
							</FormControl>
						</Grid>

						{periodType === "personalizado" && (
							<>
								<Grid item xs={12} sm={6} md={2}>
									<TextField
										fullWidth
										size="small"
										type="date"
										label="Data Inicial"
										value={startDate}
										onChange={(e) => {
											setStartDate(e.target.value);
											onChangeDateStart?.(e.target.value);
										}}
									/>
								</Grid>
								<Grid item xs={12} sm={6} md={2}>
									<TextField
										fullWidth
										size="small"
										type="date"
										label="Data Final"
										value={endDate}
										onChange={(e) => {
											setEndDate(e.target.value);
											onChangeDateEnd?.(e.target.value);
										}}
									/>
								</Grid>
							</>
						)}
					</>
				)}

				{/* STATUS */}
				{showStatusSelect && (
					<Grid item xs={12} sm={6} md={2}>
						<FormControl fullWidth size="small">
							<InputLabel id="status-label">Status</InputLabel>
							<Select labelId="status-label" value={status} label="Status" onChange={handleStatusChange}>
								<MenuItem value="todos">Todos</MenuItem>
								<MenuItem value="pending">Pendente</MenuItem>
								<MenuItem value="paid">Pago</MenuItem>
								<MenuItem value="overdue">Atrasado</MenuItem>
								<MenuItem value="canceled">Cancelado</MenuItem>
							</Select>
						</FormControl>
					</Grid>
				)}

				{/* PESQUISAR (Busca geral) */}
				{showInputSearch && (
					<Grid item xs={12} sm={6} md={3}>
						<TextField
							fullWidth
							size="small"
							placeholder="Buscar por Apólice, Inquilino..."
							value={textSearch}
							onChange={(e) => onChangeTextSearch?.(e.target.value)}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<Search />
									</InputAdornment>
								),
								endAdornment: (
									<InputAdornment position="end">
										<IconButton
											onClick={() => {
												onClearTextSearch?.();
												onChangeTextSearch?.("");
											}}
										>
											<Clear />
										</IconButton>
									</InputAdornment>
								),
							}}
						/>
					</Grid>
				)}

				{/* BOTÃO PESQUISAR */}
				<Grid item xs={12} sm={12} md="auto">
					<Box display="flex" justifyContent="flex-start" gap={1}>
						{showRefreshButton && (
							<Button variant="contained" onClick={onClickRefresh} sx={{ height: "40px" }}>
								Pesquisar
							</Button>
						)}
					</Box>
				</Grid>

				{/* BOTÃO NOVO */}
				{showNewButton && (
					<Grid item xs={12} sm="auto" md="auto" sx={{ ml: { md: "auto" } }}>
						<Box display="flex" justifyContent={isSmallScreen ? "flex-start" : "flex-end"}>
							<Button variant="contained" color="primary" startIcon={<AddOutlinedIcon />} onClick={onClickNew}>
								Novo Registro
							</Button>
						</Box>
					</Grid>
				)}
			</Grid>

			{/* INFORMAÇÃO DO PERÍODO SELECIONADO */}
			{showDateRange && (
				<Typography variant="body2" sx={{ mt: 1, ml: 1, fontStyle: "italic", color: "text.secondary" }}>
					Período em vigor: {format(parseISO(startDate), "dd/MM/yyyy")} - {format(parseISO(endDate), "dd/MM/yyyy")}
				</Typography>
			)}
		</Box>
	);
}
